import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { flexxState } from "./state";
import "./axel.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Helpers ---------------- */

const TRACK_LABELS = {
  foundational: "Beginner Track",
  intermediate: "Intermediate Track",
  advanced: "Advanced Track",
};

const GATE_STATUS_MAP = (gates, key, prevPassed) => {
  const g = gates?.[key];
  if (g?.passed) return "Passed";
  if (g?.attempts > 0) return "In progress";
  return prevPassed ? "Unlocked" : (key === "phase1" ? "Not attempted" : "Locked");
};

const RADAR_LABELS = {
  market: "Market Knowledge",
  ta: "Technical Analysis",
  strategy: "Strategy",
  risk: "Risk Management",
  psychology: "Psychology",
};

function topRadar(graph) {
  if (!graph) return { topKey: null, topVal: 0, lowKey: null, lowVal: 0 };
  const entries = Object.entries(graph);
  if (!entries.length) return { topKey: null, topVal: 0, lowKey: null, lowVal: 0 };
  let top = entries[0], low = entries[0];
  for (const e of entries) {
    if (e[1] > top[1]) top = e;
    if (e[1] < low[1]) low = e;
  }
  return { topKey: top[0], topVal: top[1], lowKey: low[0], lowVal: low[1] };
}

function buildStudentContext(user) {
  try {
    const profile = flexxState.profile() || {};
    const track = flexxState.track();
    const xp = flexxState.xp();
    const graph = flexxState.graph();
    const streak = flexxState.streak()?.days || 0;
    const diagScore = flexxState.diagnosticScore() || 0;
    const gates = flexxState.gates() || {};
    const journal = flexxState.journal() || [];
    const capstone = flexxState.capstone();

    // Gate cascade: phase1 unlocks phase2 unlocks phase3 ...
    const gateStatus = {
      phase1: GATE_STATUS_MAP(gates, "phase1", true),
      phase2: GATE_STATUS_MAP(gates, "phase2", !!gates?.phase1?.passed),
      phase3: GATE_STATUS_MAP(gates, "phase3", !!gates?.phase2?.passed),
      phase4: GATE_STATUS_MAP(gates, "phase4", !!gates?.phase3?.passed),
    };

    const courseAccess = user?.course_access || [];
    const access = courseAccess.includes("advanced") || courseAccess.includes("cfd")
      ? "Full Access"
      : (courseAccess.length ? "Beginner" : "Beginner");

    const tz = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
    })();
    // Local datetime + active trading session — gives AXEL real-time
    // coaching context (e.g. "London just opened, focus on…").
    const nowLocal = (() => {
      try {
        if (!tz) return null;
        return new Intl.DateTimeFormat("en-US", {
          timeZone: tz, weekday: "short", month: "short", day: "numeric",
          hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
        }).format(new Date());
      } catch { return null; }
    })();
    const activeSession = (() => {
      // London 07:00-16:00 UTC, NY 12:00-21:00 UTC, Asian 23:00-07:00 UTC.
      const u = new Date().getUTCHours() + new Date().getUTCMinutes() / 60;
      const inAsian = u >= 23 || u < 7;
      const inLondon = u >= 7 && u < 16;
      const inNY = u >= 12 && u < 21;
      if (inLondon && inNY) return "London+NY Overlap (highest volume)";
      if (inLondon) return "London";
      if (inNY) return "New York";
      if (inAsian) return "Asian";
      return "Off-session";
    })();

    const lastLessons = (journal || []).slice(0, 3).map((j) => j.title || j.lesson || "Trade entry").filter(Boolean);

    // Most-recent journal trade — feeds the AXEL "RECENT TRADE" block so
    // AXEL can reference what the trader actually did, not just what they
    // ask. Picks the latest entry by createdAt (most journal stores
    // newest-first but we sort defensively).
    const lastTrade = (() => {
      const sorted = [...(journal || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      const t = sorted[0];
      if (!t) return null;
      return {
        pair: t.market || null,
        direction: t.direction || null,
        session: t.session || null,
        timeframe: t.timeframe || null,
        entry: t.entry ?? null,
        sl: t.sl ?? null,
        tp: t.tp ?? null,
        rr: t.rr ?? null,
        result: t.result || null,
        grade: t.grade || null,
        risk_pct: t.super?.riskPct ?? null,
        is_open: !!t.isOpen,
        logged_at: t.createdAt || null,
      };
    })();

    return {
      name: profile.name || user?.name || null,
      track: TRACK_LABELS[track] || (track ? track : null),
      current_lesson: profile.current_lesson || null,
      overall_pct: profile.overall_pct ?? null,
      beginner_pct: profile.beginner_pct ?? null,
      beginner_done: profile.beginner_done ?? null,
      advanced_pct: profile.advanced_pct ?? null,
      advanced_done: profile.advanced_done ?? null,
      cfd_pct: profile.cfd_pct ?? null,
      cfd_done: profile.cfd_done ?? null,
      streak,
      diagnostic_score: diagScore,
      radar: graph || {},
      gates: gateStatus,
      last_active: user?.last_login || null,
      total_study_time: profile.total_study_time || null,
      last_lessons: lastLessons,
      last_trade: lastTrade,
      quiz: { average_pct: capstone?.quiz?.score || diagScore, weakest_topics: [] },
      trading_style: profile.trading_style || null,
      prop_firm_goal: profile.prop_firm_goal || null,
      preferred_pairs: profile.preferred_pairs || (profile.market ? [profile.market] : []),
      student_since: user?.created_at || null,
      access_level: access,
      timezone: tz,
      local_datetime: nowLocal,
      active_session: activeSession,
      xp,
    };
  } catch (e) {
    return { name: user?.name || null };
  }
}

function buildWelcomeMessage(ctx) {
  const name = ctx?.name;
  const streak = ctx?.streak || 0;
  const radar = ctx?.radar || {};
  const { topKey } = topRadar(radar);
  const topLabel = RADAR_LABELS[topKey] || "Technical Analysis";
  const trackLabel = ctx?.track || "your current track";
  const overall = ctx?.overall_pct;
  const gates = ctx?.gates || {};
  let nextGate = "Phase 1 Foundation";
  if (gates.phase1 === "Passed") nextGate = "Phase 2 Technical Analysis";
  if (gates.phase2 === "Passed") nextGate = "Phase 3 Risk and Strategy";
  if (gates.phase3 === "Passed") nextGate = "Phase 4 Live Execution and Scaling";
  if (gates.phase4 === "Passed") nextGate = "the Capstone certificate";

  if (name && (streak || overall != null)) {
    const overallStr = overall != null ? ` You're ${overall}% through ${trackLabel}` : ` You're working through ${trackLabel}`;
    return `Hey ${name} 🔥 You're on a ${streak}-day streak — let's keep that momentum going.${overallStr} and your strongest skill right now is ${topLabel}. Your next target is unlocking ${nextGate}. What are we working on today?`;
  }
  return `Hey, I'm AXEL — FLEXX Academy's AI trading mentor 🔥 I know the full FLEXX curriculum inside out, from complete beginner to quant-level systems. Ask me anything: trading concepts, trade reviews, prop firm prep, SageMaster setup, EA coding, prediction markets — or just tell me what you're stuck on right now. Let's get to work.`;
}

/* Lightweight markdown -> HTML for **bold**, lists, line breaks. */
function mdToHtml(s) {
  if (!s) return "";
  const esc = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // bold
  let out = esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // inline code
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  // bullet lines: lines starting with "- " or "* "
  const lines = out.split(/\n/);
  let result = [];
  let inList = false;
  for (const ln of lines) {
    if (/^\s*[-*]\s+/.test(ln)) {
      if (!inList) { result.push("<ul>"); inList = true; }
      result.push("<li>" + ln.replace(/^\s*[-*]\s+/, "") + "</li>");
    } else {
      if (inList) { result.push("</ul>"); inList = false; }
      if (ln.trim() === "") result.push("<br/>");
      else result.push(ln);
    }
  }
  if (inList) result.push("</ul>");
  return result.join("\n").replace(/\n/g, "<br/>");
}

/* ---------------- Component ---------------- */

const QUICK_CHIPS = [
  { id: "explain", label: "Explain a concept", prompt: "Explain a concept" },
  { id: "review", label: "Review my trade", prompt: "Review my trade" },
  { id: "quiz", label: "Quiz me 🎯", prompt: "Quiz me" },
  { id: "routine", label: "My daily routine 📋", prompt: "What is my daily routine?" },
  { id: "progress", label: "Review my progress 📊", prompt: "What is my progress?" },
];

export default function AxelWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]); // [{role, content, displayed}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const revealTimer = useRef(null);
  const [quota, setQuota] = useState(null); // {unlimited, limit, used, remaining}

  // Auto-open when arriving via the AXEL push nudge (?openAxel=1) or via
  // the SW FLEXX_NAV postMessage. Also accepts an optional ?axelPrompt=...
  // param (URI-encoded) that pre-fills the input box — used by the
  // Daily Briefing flashpoints "Plan with AXEL" buttons. Strips the params
  // afterward so reload doesn't reopen / re-fill.
  useEffect(() => {
    const checkAndOpen = () => {
      try {
        const url = new URL(window.location.href);
        const shouldOpen = url.searchParams.get("openAxel") === "1";
        const promptParam = url.searchParams.get("axelPrompt");
        if (shouldOpen) {
          setOpen(true);
          url.searchParams.delete("openAxel");
        }
        if (promptParam) {
          setInput(promptParam);
          url.searchParams.delete("axelPrompt");
        }
        if (shouldOpen || promptParam) {
          window.history.replaceState({}, "", url.toString());
        }
      } catch (_) { /* noop */ }
    };
    checkAndOpen();
    // Re-check whenever something dispatches a popstate (e.g. our flashpoint
    // "Plan with AXEL" buttons that set ?axelPrompt= on the URL).
    window.addEventListener("popstate", checkAndOpen);
    const onSwNav = (e) => {
      if (e?.data?.type === "FLEXX_NAV" && typeof e.data.url === "string" && e.data.url.includes("openAxel=1")) {
        setOpen(true);
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onSwNav);
    }
    return () => {
      window.removeEventListener("popstate", checkAndOpen);
      if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener("message", onSwNav);
    };
  }, []);

  // Position controls — bottom-right (default), bottom-left, top-right, hidden.
  // Persisted across reloads in localStorage.
  const [axelPos, setAxelPos] = useState(() => {
    try { return localStorage.getItem("flexx_axel_position") || "br"; } catch (_) { return "br"; }
  });
  const [axelHidden, setAxelHidden] = useState(() => {
    try { return localStorage.getItem("flexx_axel_hidden") === "1"; } catch (_) { return false; }
  });
  const [posMenuOpen, setPosMenuOpen] = useState(false);
  const setPos = (p) => {
    setAxelPos(p);
    try { localStorage.setItem("flexx_axel_position", p); } catch (_) {}
    setPosMenuOpen(false);
  };
  const setHidden = (h) => {
    setAxelHidden(h);
    try { localStorage.setItem("flexx_axel_hidden", h ? "1" : "0"); } catch (_) {}
    setPosMenuOpen(false);
  };

  // Inline style override that wins against the .axel-trigger CSS rules.
  const posStyle = useMemo(() => {
    const SAFE = "calc(80px + env(safe-area-inset-bottom, 0px))";
    if (axelPos === "bl") return { top: "auto", bottom: SAFE, left: 16, right: "auto" };
    if (axelPos === "tr") return { top: 80, bottom: "auto", right: 16, left: "auto" };
    return { top: "auto", bottom: SAFE, right: 16, left: "auto" };
  }, [axelPos]);

  // Build context once per open, refresh after each send
  const ctxRef = useRef(null);
  if (!ctxRef.current && user) ctxRef.current = buildStudentContext(user);

  // Stable session id per page load
  if (!sessionIdRef.current) {
    sessionIdRef.current = `axel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  // Welcome message + quota fetch on first open
  useEffect(() => {
    if (open && history.length === 0) {
      ctxRef.current = buildStudentContext(user);
      // Fetch quota first; if paused, show a special welcome
      axios.get(`${API}/axel/quota`, { withCredentials: true })
        .then((r) => {
          setQuota(r.data);
          if (r.data?.paused) {
            const msg = r.data.paused_reason || "AXEL is taking a short break — back soon 🔧";
            setHistory([{ role: "bot", content: msg, displayed: msg }]);
          } else {
            const welcome = buildWelcomeMessage(ctxRef.current);
            setHistory([{ role: "bot", content: welcome, displayed: welcome }]);
          }
        })
        .catch(() => {
          const welcome = buildWelcomeMessage(ctxRef.current);
          setHistory([{ role: "bot", content: welcome, displayed: welcome }]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);  

  // Autoscroll
  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, open, sending]);

  // Cleanup any reveal animation when closing
  useEffect(() => () => { if (revealTimer.current) clearInterval(revealTimer.current); }, []);

  const startReveal = (fullText) => {
    // Add an empty bot message and progressively fill it
    setHistory((h) => [...h, { role: "bot", content: fullText, displayed: "" }]);
    const tokens = fullText.split(/(\s+)/); // keep whitespace tokens
    let idx = 0;
    if (revealTimer.current) clearInterval(revealTimer.current);
    revealTimer.current = setInterval(() => {
      idx += 1;
      setHistory((h) => {
        if (h.length === 0) return h;
        const last = h[h.length - 1];
        if (last.role !== "bot") return h;
        const shown = tokens.slice(0, idx).join("");
        const next = [...h];
        next[next.length - 1] = { ...last, displayed: shown };
        return next;
      });
      if (idx >= tokens.length) {
        clearInterval(revealTimer.current);
        revealTimer.current = null;
      }
    }, 18);
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    if (quota?.paused) return; // soft-block at the UI when paused
    setError(null);
    setHistory((h) => [...h, { role: "user", content: message, displayed: message }]);
    setInput("");
    setSending(true);

    // Refresh context — student state may have advanced
    ctxRef.current = buildStudentContext(user);

    try {
      const resp = await axios.post(
        `${API}/axel/chat`,
        {
          session_id: sessionIdRef.current,
          message,
          student_context: ctxRef.current,
        },
        { withCredentials: true, timeout: 30000 }
      );
      const reply = resp?.data?.response || "I'm here. Try that again?";
      // Update quota display from server response
      if (resp?.data?.remaining_today !== undefined) {
        setQuota((q) => {
          if (!q || q.unlimited) return q;
          const r = resp.data.remaining_today;
          return { ...q, remaining: r, used: (q.limit || 0) - r };
        });
      }
      startReveal(reply);
    } catch (e) {
      console.error("AXEL error:", e);
      const status = e?.response?.status;
      if (status === 429) {
        const detail = e?.response?.data?.detail || "Daily limit reached.";
        setHistory((h) => [...h, { role: "bot", content: detail, displayed: detail }]);
        setQuota((q) => (q ? { ...q, remaining: 0, used: q.limit } : q));
        return;
      }
      const msg = e?.response?.data?.detail || "AXEL is temporarily unavailable. Try again in a moment.";
      setError({ message: msg, retryText: message });
    } finally {
      setSending(false);
      // keep focus on input
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastBotIsRevealing = useMemo(() => {
    if (!history.length) return false;
    const last = history[history.length - 1];
    return last.role === "bot" && last.displayed && last.displayed.length < last.content.length;
  }, [history]);

  if (!user) return null;

  // Hidden-state restore handle — small "AX" badge bottom-right.
  if (axelHidden) {
    return (
      <button
        type="button"
        data-testid="axel-restore-handle"
        aria-label="Show AXEL"
        onClick={() => setHidden(false)}
        style={{
          position: "fixed", zIndex: 9999,
          bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
          right: 16,
          width: 38, height: 38, borderRadius: 19,
          background: "#0C0C0C",
          border: "1px solid rgba(201, 162, 39, 0.55)",
          color: "rgba(201, 162, 39, 0.9)",
          cursor: "pointer", fontFamily: "monospace",
          fontSize: 12, fontWeight: 800, letterSpacing: 1,
          boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
          opacity: 0.7,
        }}>AX</button>
    );
  }

  return (
    <>
      <div data-testid="axel-trigger-wrapper" style={{ ...posStyle, position: "fixed", zIndex: 9999, display: open ? "none" : "block" }}>
        <button
          type="button"
          data-testid="axel-trigger-btn"
          aria-label={open ? "Close AXEL" : "Open AXEL"}
          className="axel-trigger"
          onClick={() => setOpen((o) => !o)}
          style={{ position: "static", bottom: "auto", right: "auto", left: "auto", top: "auto" }}
        >
          <span className="axel-trigger-avatar">
            AX
            <span className="axel-trigger-pulse" aria-hidden="true" />
          </span>
          <span className="axel-trigger-text">
            <span className="axel-trigger-name">AXEL</span>
            <span className="axel-trigger-sub">AI MENTOR · FLEXX</span>
          </span>
        </button>
        {/* Position / Hide menu trigger — small dot button on the trigger pill. */}
        <button
          type="button"
          data-testid="axel-pos-menu-btn"
          aria-label="Move or hide AXEL"
          onClick={(e) => { e.stopPropagation(); setPosMenuOpen((o) => !o); }}
          style={{
            position: "absolute", top: -6, right: -6,
            width: 20, height: 20, borderRadius: 10,
            background: "#0C0C0C", border: "1px solid rgba(201, 162, 39, 0.55)",
            color: "rgba(201, 162, 39, 0.85)", cursor: "pointer",
            fontSize: 11, lineHeight: 1, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
          }}>⋯</button>
        {posMenuOpen && (
          <div data-testid="axel-pos-menu"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              ...(axelPos === "tr" ? { top: 36, right: 0 } : { bottom: 64, [axelPos === "bl" ? "left" : "right"]: 0 }),
              minWidth: 180, padding: 6,
              background: "#0C0C0C", border: "1px solid rgba(201, 162, 39, 0.4)",
              borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.6)",
              display: "grid", gap: 2,
            }}>
            <PosMenuItem testid="axel-pos-br" active={axelPos === "br"} onClick={() => setPos("br")} icon="↘" label="Bottom-Right" />
            <PosMenuItem testid="axel-pos-bl" active={axelPos === "bl"} onClick={() => setPos("bl")} icon="↙" label="Bottom-Left" />
            <PosMenuItem testid="axel-pos-tr" active={axelPos === "tr"} onClick={() => setPos("tr")} icon="↗" label="Top-Right" />
            <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "4px 6px" }} />
            <PosMenuItem testid="axel-hide" active={false} onClick={() => setHidden(true)} icon="✕" label="Hide AXEL" />
          </div>
        )}
      </div>
      {posMenuOpen && (
        <div data-testid="axel-pos-menu-backdrop"
          onClick={() => setPosMenuOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
      )}

      {open && (
        <div className="axel-window" role="dialog" aria-label="AXEL — FLEXX Academy AI Mentor" data-testid="axel-window">
          <div className="axel-header">
            <div className="axel-header-left">
              <div className="axel-trigger-avatar" style={{ width: 38, height: 38, fontSize: 14 }}>AX</div>
              <div className="axel-header-title">
                <span className="axel-header-brand">FLEXX</span>
                <span className="axel-header-name" data-testid="axel-header-name">
                  AXEL <span className="axel-live-dot" aria-hidden="true" />
                </span>
                <span className="axel-header-sub">Your FLEXX AI Mentor</span>
              </div>
            </div>
            <button
              type="button"
              className="axel-close-btn"
              data-testid="axel-close-btn"
              aria-label="Close AXEL"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="axel-messages" data-testid="axel-messages">
            {history.map((m, i) => (
              <div key={i} className={`axel-msg-row ${m.role}`}>
                {m.role === "bot" && <div className="axel-msg-avatar">AX</div>}
                <div
                  className={`axel-msg ${m.role}`}
                  data-testid={m.role === "bot" ? `axel-bot-msg-${i}` : `axel-user-msg-${i}`}
                  dangerouslySetInnerHTML={
                    m.role === "bot"
                      ? { __html: mdToHtml(m.displayed || "") + (lastBotIsRevealing && i === history.length - 1 ? '<span style="opacity:0.5">▎</span>' : "") }
                      : undefined
                  }
                >
                  {m.role === "user" ? m.displayed : null}
                </div>
              </div>
            ))}
            {sending && (
              <div className="axel-msg-row bot" data-testid="axel-typing">
                <div className="axel-msg-avatar">AX</div>
                <div className="axel-typing">
                  <span className="axel-typing-dot" />
                  <span className="axel-typing-dot" />
                  <span className="axel-typing-dot" />
                  <span className="axel-typing-text">AXEL is thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {error && (
            <div className="axel-error-banner" data-testid="axel-error">
              <span>{error.message}</span>
              <button
                type="button"
                className="axel-retry-btn"
                onClick={() => { setError(null); send(error.retryText); }}
                data-testid="axel-retry-btn"
              >
                Retry
              </button>
            </div>
          )}

          <div className="axel-chips" role="toolbar" aria-label="Quick actions">
            {QUICK_CHIPS.map((c) => (
              <button
                key={c.id}
                type="button"
                className="axel-chip"
                data-testid={`axel-chip-${c.id}`}
                onClick={() => send(c.prompt)}
                disabled={sending || quota?.paused || (quota && !quota.unlimited && quota.remaining === 0)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {quota && !quota.unlimited && (
            <div
              data-testid="axel-quota-display"
              style={{
                padding: "6px 14px 0",
                fontSize: 10,
                color: quota.remaining <= 5 ? "#FF6B35" : "rgba(170,170,170,0.6)",
                letterSpacing: 0.5,
                fontFamily: "monospace",
                textAlign: "right",
              }}
            >
              {quota.remaining > 0
                ? `${quota.remaining}/${quota.limit} messages left today`
                : "Daily limit reached — back tomorrow 🔥"}
            </div>
          )}

          <form
            className="axel-input-bar"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <textarea
              ref={inputRef}
              data-testid="axel-input"
              rows={1}
              className="axel-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask AXEL anything about trading..."
              aria-label="Message AXEL"
            />
            <button
              type="submit"
              className="axel-send-btn"
              data-testid="axel-send-btn"
              disabled={sending || !input.trim()}
              aria-label="Send"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}


function PosMenuItem({ testid, active, onClick, icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        all: "unset", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 10px", borderRadius: 6,
        background: active ? "rgba(201, 162, 39, 0.16)" : "transparent",
        color: active ? "rgba(232, 185, 79, 1)" : "rgba(232, 232, 220, 0.85)",
        fontFamily: "monospace", fontSize: 11.5, letterSpacing: 0.5,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(201, 162, 39, 0.08)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
      <span style={{ width: 14, textAlign: "center", color: "rgba(201, 162, 39, 0.9)" }}>{icon}</span>
      <span>{label}</span>
      {active && <span style={{ marginLeft: "auto", color: "rgba(201, 162, 39, 0.9)", fontSize: 10 }}>✓</span>}
    </button>
  );
}
